import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { Container } from '../Container';
import { GoldDivider } from '../GoldDivider';
import { TrophyMark } from '../TrophyMark';

export type FooterLink = {
  label: string;
  href: string;
  external?: boolean;
};

type FooterProps = {
  tagline?: ReactNode;
  institutionalLinks?: FooterLink[];
  authenticityBlock?: ReactNode;
  fineprint?: ReactNode;
  compact?: boolean;
  className?: string;
};

const DEFAULT_LINKS: FooterLink[] = [
  { label: 'Termos de uso', href: '#' },
  { label: 'Política de privacidade', href: '#' },
  { label: 'Contato', href: 'mailto:contato@eternalize.tech' },
];

const DEFAULT_TAGLINE = 'Sua presença, eternizada na taça da Copa do Mundo Feminina FIFA 2027.';
const DEFAULT_AUTHENTICITY =
  'Registros protegidos por tecnologia blockchain — únicos, permanentes e verificáveis publicamente.';
const DEFAULT_FINEPRINT =
  'Dados de registros são públicos. E-mail e identidade de conta permanecem privados, em conformidade com a LGPD.';

export function Footer({
  tagline = DEFAULT_TAGLINE,
  institutionalLinks = DEFAULT_LINKS,
  authenticityBlock = DEFAULT_AUTHENTICITY,
  fineprint = DEFAULT_FINEPRINT,
  compact = false,
  className,
}: FooterProps) {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      id="rodape"
      className={clsx(
        'relative border-t border-gold/15 bg-night text-neutral-300',
        compact ? 'py-10' : 'py-16',
        className,
      )}
    >
      <Container size="wide">
        <div
          className={clsx(
            'grid grid-cols-1 gap-12',
            !compact && 'md:grid-cols-3 md:gap-8',
            compact && 'md:grid-cols-2 md:gap-8',
          )}
        >
          {/* Marca */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <TrophyMark variant="filled" size="sm" />
              <div>
                <p className="font-display text-lg text-parchment">Touch the Trophy</p>
                <p className="font-mono text-xs uppercase tracking-stamp text-champagne">
                  Eternalize Tech
                </p>
              </div>
            </div>
            {tagline ? <p className="text-sm leading-relaxed">{tagline}</p> : null}
          </div>

          {/* Links institucionais */}
          {institutionalLinks.length > 0 ? (
            <nav aria-label="Links institucionais" className="flex flex-col gap-3">
              <p className="font-mono text-xs uppercase tracking-stamp text-gold">Institucional</p>
              <ul className="flex flex-col gap-2 text-sm">
                {institutionalLinks.map((link) => (
                  <li key={`${link.label}-${link.href}`}>
                    <a
                      href={link.href}
                      className="transition-colors duration-fast hover:text-champagne"
                      {...(link.external
                        ? { target: '_blank', rel: 'noreferrer noopener' }
                        : link.href === '#'
                          ? { 'aria-disabled': true }
                          : {})}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}

          {/* Autenticidade — opcional em modo compact */}
          {!compact && authenticityBlock ? (
            <div className="flex flex-col gap-3">
              <p className="font-mono text-xs uppercase tracking-stamp text-gold">Autenticidade</p>
              <p className="text-sm leading-relaxed">{authenticityBlock}</p>
            </div>
          ) : null}
        </div>

        <GoldDivider variant="line" className={compact ? 'my-6' : 'my-10'} />

        <div className="flex flex-col gap-3 text-xs md:flex-row md:items-center md:justify-between">
          <p>
            © {currentYear} Eternalize Tech · Touch the Trophy · Copa Feminina FIFA 2027.
          </p>
          {fineprint ? <p className="text-neutral-300/80">{fineprint}</p> : null}
        </div>
      </Container>
    </footer>
  );
}
